import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { formsApi, patientApi } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import AgencyProfileDialog from '../../components/forms/AgencyProfileDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, Plus, Edit, Trash2, Loader2, Search,
  Upload, Sparkles, Eye, Users, CheckCircle, Clock, Archive,
  Download, GitBranch, Play, ChevronRight, ArrowUp, ArrowDown,
  User, GripVertical, LayoutTemplate, Share2, Globe, Building2, Shield, Copy
, Library } from 'lucide-react';
import FORM_TEMPLATES from './formTemplates';
import OfflineQueuePanel from '../../components/OfflineQueuePanel';

const STATUS_CFG = {
  draft: { color: '#64748B', icon: Clock, label: 'Draft' },
  published: { color: '#166534', icon: CheckCircle, label: 'Published' },
  archived: { color: '#92400E', icon: Archive, label: 'Archived' },
};
const ROLE_LABELS = { super_admin: 'Super Admin', practice_admin: 'Practice Admin', physician: 'Physician', nurse: 'Nurse', billing_specialist: 'Billing', patient: 'Patient' };

export default function FormsListPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showDelete, setShowDelete] = useState(null);
  const [showAiUpload, setShowAiUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preserveLayout, setPreserveLayout] = useState(true);
  const [publishMode, setPublishMode] = useState('review');
  const [stripMarkup, setStripMarkup] = useState(true);
  const [showAgency, setShowAgency] = useState(false);
  const [aiProgress, setAiProgress] = useState(null); // {message, total, completed} | null
  const [aiBackground, setAiBackground] = useState(false); // user clicked "Run in background"
  const aiBackgroundRef = React.useRef(false);
  const [tab, setTab] = useState('forms');
  const [workflows, setWorkflows] = useState([]);
  const [loadingWf, setLoadingWf] = useState(false);
  const [showCreateWf, setShowCreateWf] = useState(false);
  const [wfName, setWfName] = useState('');
  const [wfDesc, setWfDesc] = useState('');
  const [wfChain, setWfChain] = useState([]);
  const [wfTrigger, setWfTrigger] = useState('manual');
  const [savingWf, setSavingWf] = useState(false);
  const [showExport, setShowExport] = useState(null); // form object
  const [exportFields, setExportFields] = useState([]);
  const [includePatient, setIncludePatient] = useState(false);
  const [exportPatientId, setExportPatientId] = useState('');
  const [exportTabs, setExportTabs] = useState(['overview', 'hospital', 'concern', 'caregiver', 'pharmacy']);
  const [patients, setPatients] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showShare, setShowShare] = useState(null);
  const [shareType, setShareType] = useState('all');
  const [shareOrgIds, setShareOrgIds] = useState([]);
  const [shareRoles, setShareRoles] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientIdParam = searchParams.get('patientId') || '';

  const PATIENT_TABS = [
    { key: 'overview', label: 'Contact & Insurance' },
    { key: 'hospital', label: 'Hospital & Medical History' },
    { key: 'concern', label: 'Chief Concern' },
    { key: 'caregiver', label: 'Primary Caregiver' },
    { key: 'pharmacy', label: 'Pharmacy' },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await formsApi.list(params);
      setForms(res.data.forms || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadWorkflows = async () => {
    setLoadingWf(true);
    try {
      const res = await formsApi.workflows.list();
      setWorkflows(res.data.workflows || []);
    } catch (err) { console.error(err); }
    finally { setLoadingWf(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { if (tab === 'workflows') loadWorkflows(); }, [tab]);

  const handleDelete = async () => {
    try {
      await formsApi.delete(showDelete);
      toast.success('Form deleted');
      setShowDelete(null);
      load();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleDuplicate = async (form) => {
    try {
      const res = await api.post(`/forms/${form.id}/duplicate`);
      toast.success(`Duplicated — "${res.data.name}"`);
      load();
    } catch { toast.error('Failed to duplicate form'); }
  };

  const handleAiUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setAiBackground(false);
    aiBackgroundRef.current = false;
    setAiProgress({ message: 'Uploading…', total: null, completed: 0 });
    const fileName = file.name;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('publish_mode', publishMode);
      fd.append('strip_markup', stripMarkup ? 'auto' : 'off');
      let res;
      const isPdf = (fileName || '').toLowerCase().endsWith('.pdf');
      const shouldAbort = () => aiBackgroundRef.current;
      if (preserveLayout && isPdf) {
        res = await formsApi.aiGeneratePdfLayout(fd, setAiProgress, shouldAbort);
      } else {
        res = await formsApi.aiGenerate(fd);
      }
      const gen = res.data.generated_form;
      const msg = preserveLayout && isPdf
        ? `Imported "${gen.name}" — ${gen.fields?.length || 0} fields across ${res.data.page_count || 1} page(s). Layout preserved for print.`
        : `AI generated form: "${gen.name}" with ${gen.fields?.length || 0} fields`;
      setShowAiUpload(false);
      if (res.data.form_id) {
        toast.success(`Published "${gen.name}" — ${gen.fields?.length || 0} fields, linked to patient records`);
        load();
        return;
      }
      toast.success(msg);
      navigate('/forms/builder', { state: { aiForm: gen } });
    } catch (err) {
      // Aborted by "Run in background" — backend keeps running and will
      // post a notification when done. Don't show an error.
      if (err?.aborted) {
        toast.info(`"${fileName}" is still processing — we'll notify you when it's ready.`, { duration: 5000 });
      } else {
        const detail = err.response?.data?.detail || err.message || 'AI generation failed';
        // Budget-exceeded message is long + actionable — give the user time
        // to read it. Other errors get the default 4 s.
        const isBudget = /budget/i.test(detail);
        toast.error(detail, { duration: isBudget ? 12000 : 5000 });
      }
    } finally {
      setUploading(false);
      setAiProgress(null);
      setAiBackground(false);
      aiBackgroundRef.current = false;
    }
  };

  const runInBackground = () => {
    aiBackgroundRef.current = true;
    setAiBackground(true);
    setShowAiUpload(false);
  };

  const openExportDialog = async (form) => {
    setShowExport(form);
    setIncludePatient(false);
    setExportPatientId('');
    setExportTabs(['overview', 'hospital', 'concern', 'caregiver', 'pharmacy']);
    // Load form fields for reordering
    try {
      const res = await formsApi.get(form.id);
      setExportFields((res.data.fields || []).map((f, i) => ({ ...f, _order: i })));
    } catch { setExportFields([]); }
    // Load patients
    try {
      const res = await patientApi.list({ limit: 100 });
      setPatients(res.data || []);
    } catch { setPatients([]); }
  };

  const moveExportField = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= exportFields.length) return;
    const arr = [...exportFields];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setExportFields(arr);
  };

  const toggleExportTab = (key) => {
    setExportTabs(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleExportPdf = async () => {
    if (!showExport) return;
    setExporting(true);
    try {
      const options = {
        include_patient_info: includePatient,
        patient_id: includePatient ? exportPatientId : null,
        patient_tabs: includePatient ? exportTabs : [],
        field_order: exportFields.map(f => f.field_id),
      };
      const res = await formsApi.exportPdf(showExport.id, options);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${showExport.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
      setShowExport(null);
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  const handleCreateWorkflow = async () => {
    if (!wfName || wfChain.length < 2) { toast.error('Name and at least 2 forms required'); return; }
    setSavingWf(true);
    try {
      await formsApi.workflows.create({ name: wfName, description: wfDesc, form_chain: wfChain, trigger: wfTrigger });
      toast.success('Workflow created');
      setShowCreateWf(false);
      setWfName(''); setWfDesc(''); setWfChain([]); setWfTrigger('manual');
      loadWorkflows();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create workflow'); }
    finally { setSavingWf(false); }
  };

  const handleDeleteWorkflow = async (id) => {
    try {
      await formsApi.workflows.delete(id);
      toast.success('Workflow deleted');
      loadWorkflows();
    } catch { toast.error('Failed to delete workflow'); }
  };

  const addToChain = (formId) => {
    if (!wfChain.includes(formId)) setWfChain([...wfChain, formId]);
  };
  const removeFromChain = (idx) => setWfChain(wfChain.filter((_, i) => i !== idx));

  const openShareDialog = async (form) => {
    setShowShare(form);
    setShareType(form.share_type || 'all');
    setShareOrgIds(form.shared_org_ids || []);
    setShareRoles(form.shared_role_groups || []);
    try { const res = await api.get('/organizations'); setOrgs(res.data || []); } catch { setOrgs([]); }
  };

  const handleShare = async () => {
    if (!showShare) return;
    try {
      await formsApi.share(showShare.id, { share_type: shareType, organization_ids: shareOrgIds, role_groups: shareRoles });
      toast.success(`Form shared (${shareType})`);
      setShowShare(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to share'); }
  };

  const handleUnshare = async (formId) => {
    try { await formsApi.unshare(formId); toast.success('Sharing removed'); load(); } catch { toast.error('Failed'); }
  };

  const toggleShareOrg = (id) => setShareOrgIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleShareRole = (r) => setShareRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);

  const filtered = forms.filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="forms-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Forms</h1>
          <p className="text-[#64748B] mt-1">Create, manage, and assign forms to roles</p>
        </div>
        <div className="flex gap-2">
          {['super_admin', 'practice_admin'].includes(currentUser?.role) && (
            <Button variant="outline" onClick={() => setShowAgency(true)} data-testid="agency-profile-btn">Agency Details</Button>
          )}
          {['super_admin', 'practice_admin', 'director_patient_services'].includes(currentUser?.role) && (
            <Link to="/forms/library"><Button variant="outline" className="border-[#0E7490] text-[#0E7490] hover:bg-[#F0FDFA]" data-testid="form-library-btn"><Library className="w-4 h-4 mr-2" />Form Library</Button></Link>
          )}
          <Button variant="outline" onClick={() => setShowAiUpload(true)} data-testid="ai-upload-btn">
            <Sparkles className="w-4 h-4 mr-2 text-[#0E7490]" />AI from File
          </Button>
          <Link to="/forms/builder">
            <Button className="careiq-btn-primary" data-testid="create-form-btn"><Plus className="w-4 h-4 mr-2" />New Form</Button>
          </Link>
        </div>
      </div>

      <OfflineQueuePanel title="Pending Sync" />

      {/* Tabs */}
      <div className="flex border-b border-[#E2E8F0]">
        <button onClick={() => setTab('forms')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'forms' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-forms">Forms</button>
        <button onClick={() => setTab('templates')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'templates' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-templates">Templates</button>
        <button onClick={() => setTab('workflows')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'workflows' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-workflows">Workflows</button>
      </div>

      {tab === 'forms' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input placeholder="Search forms..." value={search} onChange={(e) => setSearch(e.target.value)} className="careiq-input pl-10" data-testid="form-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
          ) : filtered.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((f) => {
                const cfg = STATUS_CFG[f.status] || STATUS_CFG.draft;
                const Icon = cfg.icon;
                return (
                  <Card key={f.id} className="careiq-card hover:border-[#0E7490] transition-colors" data-testid={`form-card-${f.id}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#0A2342] truncate">{f.name}</h3>
                          {f.description && <p className="text-xs text-[#64748B] mt-0.5 line-clamp-2">{f.description}</p>}
                        </div>
                        <span className="flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ml-2" style={{ backgroundColor: cfg.color + '15', color: cfg.color }}>
                          <Icon className="w-3 h-3" />{cfg.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {f.category && <span className="text-[10px] px-2 py-0.5 bg-[#0E7490]/10 text-[#0E7490] rounded-full">{f.category}</span>}
                        <span className="text-[10px] px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full">{f.fields_count} fields</span>
                        <span className="text-[10px] px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full">v{f.version}</span>
                      </div>
                      {f.assigned_roles?.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Users className="w-3 h-3 text-[#64748B]" />
                          {f.assigned_roles.map((r, i) => (
                            <span key={i} className="text-[9px] px-1.5 py-0.5 bg-[#1A56DB]/10 text-[#1A56DB] rounded">{ROLE_LABELS[r] || r}</span>
                          ))}
                        </div>
                      )}
                      {f.shared && (
                        <div className="flex items-center gap-1">
                          <Globe className="w-3 h-3 text-[#166534]" />
                          <span className="text-[9px] text-[#166534] font-medium">
                            Shared {f.share_type === 'all' ? '(All Orgs)' : f.share_type === 'orgs' ? `(${f.shared_org_ids?.length} org${f.shared_org_ids?.length !== 1 ? 's' : ''})` : `(${f.shared_role_groups?.join(', ')})`}
                          </span>
                        </div>
                      )}
                      {(f.created_at || f.updated_at) && (
                        <div className="flex flex-col gap-0.5 text-[10px] text-[#94A3B8] pt-1 border-t border-[#F1F5F9]" data-testid={`form-timestamps-${f.id}`}>
                          {f.created_at && <span>Created {new Date(f.created_at).toLocaleString()}</span>}
                          {f.updated_at && f.updated_at !== f.created_at && <span>Updated {new Date(f.updated_at).toLocaleString()}</span>}
                        </div>
                      )}
                      <div className="flex gap-1 pt-1">
                        <Link to={`/forms/builder?id=${f.id}`} className="flex-1">
                          <Button size="sm" variant="outline" className="w-full" data-testid={`edit-form-${f.id}`}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                        </Link>
                        <Link to={`/forms/${f.id}/fill${patientIdParam ? `?patientId=${patientIdParam}` : ''}`} className="flex-1">
                          <Button size="sm" variant="outline" className="w-full text-[#0E7490]"><FileText className="w-3 h-3 mr-1" />Fill</Button>
                        </Link>
                        <Button size="sm" variant="ghost" onClick={() => openExportDialog(f)} data-testid={`export-pdf-${f.id}`} title="Export PDF">
                          <Download className="w-3.5 h-3.5 text-[#0E7490]" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDuplicate(f)} data-testid={`duplicate-form-${f.id}`} title="Duplicate">
                          <Copy className="w-3.5 h-3.5 text-[#0E7490]" />
                        </Button>
                        {isSuperAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => f.shared ? handleUnshare(f.id) : openShareDialog(f)} data-testid={`share-form-${f.id}`} title={f.shared ? 'Unshare' : 'Share'}>
                            <Share2 className={`w-3.5 h-3.5 ${f.shared ? 'text-[#166534]' : 'text-[#64748B]'}`} />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setShowDelete(f.id)}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="careiq-card"><CardContent className="py-16 text-center text-[#64748B]"><FileText className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" /><p>No forms yet. Create one or upload a file for AI generation.</p></CardContent></Card>
          )}
        </>
      )}

      {tab === 'templates' && (
        <div className="space-y-4">
          <p className="text-sm text-[#64748B]">Start from a professionally designed healthcare form template</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FORM_TEMPLATES.map((tmpl) => (
              <Card key={tmpl.id} className="careiq-card overflow-hidden hover:shadow-md transition-shadow cursor-pointer group" data-testid={`template-${tmpl.id}`}>
                <div className="h-2" style={{ backgroundColor: tmpl.color }} />
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tmpl.color + '15' }}>
                      <LayoutTemplate className="w-5 h-5" style={{ color: tmpl.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#0A2342] text-sm">{tmpl.name}</h3>
                      <p className="text-xs text-[#64748B] mt-0.5">{tmpl.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: tmpl.color }}>{tmpl.category}</span>
                    <span className="text-[10px] px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full">{tmpl.fields.length} fields</span>
                    <span className="text-[10px] px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full">{tmpl.fields.filter(f => f.field_type === 'section').length} sections</span>
                  </div>
                  <Button className="w-full text-white text-xs group-hover:opacity-90" style={{ backgroundColor: tmpl.color }} onClick={() => navigate('/forms/builder', { state: { aiForm: { name: tmpl.name, description: tmpl.description, category: tmpl.category, fields: tmpl.fields.map((f, i) => ({ ...f, field_id: `field_${i}`, order: i })) } } })} data-testid={`use-template-${tmpl.id}`}>
                    <Plus className="w-3 h-3 mr-1" />Use This Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === 'workflows' && (
        <>
          <div className="flex justify-end">
            <Button className="careiq-btn-primary" onClick={() => { setShowCreateWf(true); setWfChain([]); }} data-testid="create-workflow-btn">
              <GitBranch className="w-4 h-4 mr-2" />New Workflow
            </Button>
          </div>
          {loadingWf ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
          ) : workflows.length > 0 ? (
            <div className="space-y-3">
              {workflows.map((wf) => (
                <Card key={wf.id} className="careiq-card" data-testid={`workflow-card-${wf.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-[#0A2342]">{wf.name}</h3>
                        {wf.description && <p className="text-xs text-[#64748B]">{wf.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 bg-[#0E7490]/10 text-[#0E7490] rounded-full">{wf.trigger}</span>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteWorkflow(wf.id)} data-testid={`delete-wf-${wf.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-[#991B1B]" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {wf.form_chain?.map((form, idx) => (
                        <React.Fragment key={idx}>
                          <span className="text-xs px-2 py-1 bg-[#F1F5F9] rounded-md text-[#0A2342] font-medium">{form.name}</span>
                          {idx < wf.form_chain.length - 1 && <ChevronRight className="w-3 h-3 text-[#94A3B8]" />}
                        </React.Fragment>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="careiq-card"><CardContent className="py-16 text-center text-[#64748B]"><GitBranch className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" /><p>No workflows yet. Chain forms together to create automated workflows.</p></CardContent></Card>
          )}
        </>
      )}

      {/* AI Upload Dialog */}
      <Dialog open={showAiUpload} onOpenChange={(open) => {
        if (!open && uploading) {
          // Treat dismiss-while-uploading as "Run in background" — the backend
          // job keeps running and a notification surfaces when it's done.
          runInBackground();
        } else {
          setShowAiUpload(open);
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="ai-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#0E7490]" />AI Form Generator</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#64748B]">Upload a document (PDF, TXT, CSV) and AI will analyze it to create a form structure automatically.</p>

            {/* Preserve layout option — PDF only */}
            <label className="flex items-start gap-2 p-3 rounded-lg border border-[#E2E8F0] cursor-pointer hover:border-[#0E7490] transition-colors" data-testid="preserve-layout-label">
              <input
                type="checkbox"
                checked={preserveLayout}
                onChange={e => setPreserveLayout(e.target.checked)}
                className="mt-0.5"
                data-testid="preserve-layout-checkbox"
                disabled={uploading}
              />
              <span className="text-xs leading-relaxed">
                <span className="font-semibold text-[#0A2342] block">Preserve original PDF layout for printing</span>
                <span className="text-[#64748B]">When enabled, the AI also captures each field's position so filled submissions can be printed to look <strong>exactly like the original paper form</strong>. PDF-only. Up to 10 pages.</span>
              </span>
            </label>

            {preserveLayout && (
              <div className="space-y-2" data-testid="ai-publish-options">
                <div className="grid grid-cols-2 gap-2">
                  {[['review', 'Review mapping first', 'Opens in the builder so you can fix any linked field'], ['auto', 'Publish right away', 'Staff can use it as soon as the AI finishes']].map(([v, t, d]) => (
                    <button key={v} type="button" disabled={uploading} onClick={() => setPublishMode(v)} className={`text-left p-2.5 rounded-lg border transition-colors ${publishMode === v ? 'border-[#0E7490] bg-[#F0FDFA]' : 'border-[#E2E8F0] hover:border-[#0E7490]/50'}`} data-testid={`publish-mode-${v}`}>
                      <span className="block text-xs font-semibold text-[#0A2342]">{t}</span>
                      <span className="block text-[11px] text-[#64748B] leading-snug mt-0.5">{d}</span>
                    </button>
                  ))}
                </div>
                <label className="flex items-start gap-2 text-xs text-[#64748B] cursor-pointer">
                  <input type="checkbox" checked={stripMarkup} onChange={e => setStripMarkup(e.target.checked)} disabled={uploading} className="mt-0.5" data-testid="strip-markup-checkbox" />
                  <span>Remove handwriting / filled-in entries from scanned copies so the form starts blank</span>
                </label>
              </div>
            )}

            <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-[#E2E8F0] rounded-xl cursor-pointer hover:border-[#0E7490] transition-colors" data-testid="ai-file-input-label">
              {uploading ? (
                <div className="w-full flex flex-col items-center" data-testid="ai-upload-progress">
                  <Loader2 className="w-8 h-8 text-[#0E7490] animate-spin mb-2" />
                  <span className="text-sm text-[#0E7490] font-medium">
                    {aiProgress?.message || (preserveLayout ? 'Extracting layout + fields…' : 'Analyzing with AI…')}
                  </span>
                  {aiProgress?.total > 0 && (
                    <div className="w-full max-w-xs mt-3" data-testid="ai-upload-progress-bar">
                      <div className="h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0E7490] transition-all duration-500 ease-out"
                          style={{ width: `${Math.round((aiProgress.completed / aiProgress.total) * 100)}%` }}
                        />
                      </div>
                      <div className="text-xs text-[#64748B] mt-1.5 text-center">
                        {aiProgress.completed} of {aiProgress.total} {aiProgress.total === 1 ? 'page' : 'pages'} • {Math.round((aiProgress.completed / aiProgress.total) * 100)}%
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <><Upload className="w-8 h-8 text-[#94A3B8] mb-2" /><span className="text-sm text-[#64748B]">Click to upload a file</span><span className="text-xs text-[#94A3B8] mt-1">{preserveLayout ? 'PDF only (layout preserved)' : 'PDF, TXT, CSV supported'}</span></>
              )}
              <input type="file" className="hidden" accept={preserveLayout ? '.pdf' : '.pdf,.txt,.csv,.md,.doc,.docx'} onChange={handleAiUpload} disabled={uploading} data-testid="ai-file-input" />
            </label>

            {uploading && (
              <div className="flex items-center justify-between gap-3 pt-1">
                <p className="text-xs text-[#64748B]">Big PDF? You can close this dialog — we'll send a notification when it's ready.</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-[#0E7490] text-[#0E7490] hover:bg-[#F0FDFA] shrink-0"
                  onClick={runInBackground}
                  disabled={aiBackground}
                  data-testid="ai-run-in-background-btn"
                >
                  Run in background
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Workflow Dialog */}
      <Dialog open={showCreateWf} onOpenChange={setShowCreateWf}>
        <DialogContent className="sm:max-w-lg" data-testid="create-workflow-dialog">
          <DialogHeader><DialogTitle>Create Workflow</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Workflow Name *</Label><Input value={wfName} onChange={(e) => setWfName(e.target.value)} placeholder="e.g. Patient Onboarding" data-testid="wf-name-input" /></div>
            <div><Label>Description</Label><Input value={wfDesc} onChange={(e) => setWfDesc(e.target.value)} placeholder="Brief description" /></div>
            <div>
              <Label>Trigger</Label>
              <Select value={wfTrigger} onValueChange={setWfTrigger}>
                <SelectTrigger data-testid="wf-trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="on_submit">On Form Submit</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Form Chain (add forms in order) *</Label>
              <div className="space-y-2 mt-1">
                {wfChain.map((fid, idx) => {
                  const f = forms.find(x => x.id === fid);
                  return (
                    <div key={idx} className="flex items-center gap-2 bg-[#F1F5F9] rounded-md px-3 py-1.5">
                      <span className="w-5 h-5 bg-[#0E7490] text-white rounded-full text-[10px] flex items-center justify-center">{idx + 1}</span>
                      <span className="text-sm flex-1">{f?.name || fid}</span>
                      <button onClick={() => removeFromChain(idx)} className="text-[#991B1B]"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  );
                })}
              </div>
              <Select onValueChange={addToChain}>
                <SelectTrigger className="mt-2" data-testid="wf-add-form-select"><SelectValue placeholder="Add form to chain..." /></SelectTrigger>
                <SelectContent>{forms.filter(f => f.status === 'published').map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreateWf(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleCreateWorkflow} disabled={savingWf} data-testid="save-workflow-btn">
                {savingWf ? 'Creating...' : 'Create Workflow'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Form */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-[#991B1B]">Delete Form</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] mt-2">This will permanently delete the form and all its submissions.</p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDelete(null)}>Cancel</Button>
            <Button className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white" onClick={handleDelete} data-testid="confirm-delete-form-btn">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export PDF Dialog */}
      <Dialog open={!!showExport} onOpenChange={() => setShowExport(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="export-pdf-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-[#0E7490]" />Export to PDF</DialogTitle></DialogHeader>
          {showExport && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-[#0A2342] font-medium">{showExport.name}</div>

              {/* Include Patient Info */}
              <div className="border border-[#E2E8F0] rounded-lg p-3 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={includePatient} onChange={(e) => setIncludePatient(e.target.checked)} className="rounded" />
                  <User className="w-4 h-4 text-[#0E7490]" />
                  <span className="text-sm font-medium text-[#0A2342]">Include Patient Information</span>
                </label>
                {includePatient && (
                  <div className="ml-6 space-y-3">
                    <div>
                      <Label className="text-xs">Select Patient</Label>
                      <Select value={exportPatientId} onValueChange={setExportPatientId}>
                        <SelectTrigger data-testid="export-patient-select"><SelectValue placeholder="Choose patient..." /></SelectTrigger>
                        <SelectContent>
                          {patients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.mrn}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Include Tabs</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {PATIENT_TABS.map(tab => (
                          <button key={tab.key} onClick={() => toggleExportTab(tab.key)} className={`text-xs px-3 py-1 rounded-full border transition-colors ${exportTabs.includes(tab.key) ? 'bg-[#0E7490] text-white border-[#0E7490]' : 'bg-white text-[#64748B] border-[#E2E8F0]'}`} data-testid={`export-tab-${tab.key}`}>
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Reorder Fields */}
              <div className="border border-[#E2E8F0] rounded-lg p-3">
                <Label className="text-xs text-[#0A2342] font-medium">Reorder Form Sections</Label>
                <p className="text-[10px] text-[#94A3B8] mb-2">Move sections up/down to change their order in the exported PDF</p>
                <ScrollArea className="max-h-48">
                  <div className="space-y-1">
                    {exportFields.map((field, idx) => (
                      <div key={field.field_id} className="flex items-center gap-2 p-1.5 rounded bg-[#F8FAFC] text-xs" data-testid={`export-field-${idx}`}>
                        <GripVertical className="w-3 h-3 text-[#D1D5DB] shrink-0" />
                        <span className="flex-1 text-[#0A2342] truncate">{field.label || 'Untitled'}</span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-[#0E7490]/10 text-[#0E7490] rounded">{field.field_type}</span>
                        <button onClick={() => moveExportField(idx, -1)} className="text-[#94A3B8] hover:text-[#0A2342]" data-testid={`move-up-${idx}`}><ArrowUp className="w-3 h-3" /></button>
                        <button onClick={() => moveExportField(idx, 1)} className="text-[#94A3B8] hover:text-[#0A2342]" data-testid={`move-down-${idx}`}><ArrowDown className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowExport(null)}>Cancel</Button>
                <Button className="careiq-btn-primary" onClick={handleExportPdf} disabled={exporting || (includePatient && !exportPatientId)} data-testid="confirm-export-pdf-btn">
                  {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</> : <><Download className="w-4 h-4 mr-2" />Export PDF</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Form Dialog */}
      <Dialog open={!!showShare} onOpenChange={() => setShowShare(null)}>
        <DialogContent className="sm:max-w-md" data-testid="share-form-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[#0E7490]" />Share Form</DialogTitle></DialogHeader>
          {showShare && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-[#0A2342] font-medium">{showShare.name}</div>

              <div>
                <Label className="text-xs">Share With</Label>
                <div className="space-y-2 mt-1">
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${shareType === 'all' ? 'border-[#0E7490] bg-[#0E7490]/5' : 'border-[#E2E8F0]'}`}>
                    <input type="radio" name="shareType" checked={shareType === 'all'} onChange={() => setShareType('all')} />
                    <div><Globe className="w-4 h-4 text-[#0E7490] inline mr-1" /><span className="text-sm font-medium">All Organizations</span><p className="text-[10px] text-[#64748B]">Every organization on the platform can access this form</p></div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${shareType === 'orgs' ? 'border-[#0E7490] bg-[#0E7490]/5' : 'border-[#E2E8F0]'}`}>
                    <input type="radio" name="shareType" checked={shareType === 'orgs'} onChange={() => setShareType('orgs')} />
                    <div><Building2 className="w-4 h-4 text-[#0E7490] inline mr-1" /><span className="text-sm font-medium">Specific Organizations</span><p className="text-[10px] text-[#64748B]">Only selected organizations can see this form</p></div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${shareType === 'roles' ? 'border-[#0E7490] bg-[#0E7490]/5' : 'border-[#E2E8F0]'}`}>
                    <input type="radio" name="shareType" checked={shareType === 'roles'} onChange={() => setShareType('roles')} />
                    <div><Shield className="w-4 h-4 text-[#0E7490] inline mr-1" /><span className="text-sm font-medium">Specific Role Groups</span><p className="text-[10px] text-[#64748B]">Only users with selected roles can access</p></div>
                  </label>
                </div>
              </div>

              {shareType === 'orgs' && (
                <div>
                  <Label className="text-xs">Select Organizations</Label>
                  <div className="max-h-36 overflow-y-auto mt-1 space-y-1 border border-[#E2E8F0] rounded-lg p-2">
                    {orgs.map((o) => (
                      <label key={o.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${shareOrgIds.includes(o.id) ? 'bg-[#0E7490]/10' : 'hover:bg-[#F8FAFC]'}`}>
                        <input type="checkbox" checked={shareOrgIds.includes(o.id)} onChange={() => toggleShareOrg(o.id)} className="rounded" />
                        <Building2 className="w-3 h-3 text-[#64748B]" /><span className="text-sm">{o.name}</span>
                      </label>
                    ))}
                    {orgs.length === 0 && <p className="text-xs text-[#94A3B8] text-center py-2">No organizations found</p>}
                  </div>
                </div>
              )}

              {shareType === 'roles' && (
                <div>
                  <Label className="text-xs">Select Role Groups</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['practice_admin', 'physician', 'nurse', 'billing_specialist', 'patient'].map(role => (
                      <button key={role} onClick={() => toggleShareRole(role)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${shareRoles.includes(role) ? 'bg-[#0E7490] text-white border-[#0E7490]' : 'bg-white text-[#64748B] border-[#E2E8F0]'}`}>
                        {ROLE_LABELS[role] || role}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowShare(null)}>Cancel</Button>
                <Button className="careiq-btn-primary" onClick={handleShare} data-testid="confirm-share-btn">
                  <Share2 className="w-4 h-4 mr-2" />Share Form
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AgencyProfileDialog open={showAgency} onOpenChange={setShowAgency} />
    </div>
  );
}
